import { handleize } from '@theme/wc-card-preview';

export const QUICK_ADD_SECTION_ID = 'section-rendering-quick-add';
export const MOBILE_QUICK_ADD_SELECTOR = '[data-wc-quick-add-mobile]';

/**
 * @param {string} productUrl
 * @param {string[]} [extraParams]
 * @returns {string}
 */
export function buildMobileQuickAddUrl(productUrl, extraParams = []) {
  const [path, query = ''] = String(productUrl || '').split('?');
  const search = new URLSearchParams(query);
  const params = [];
  const variant = search.get('variant');
  const view = search.get('view');
  if (variant) params.push(`variant=${encodeURIComponent(variant)}`);
  if (view) params.push(`view=${encodeURIComponent(view)}`);
  params.push(`section_id=${QUICK_ADD_SECTION_ID}`);
  params.push(...extraParams.filter(Boolean));
  return `${path}?${params.join('&')}`;
}

/**
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
export function isMobileQuickAddPicker(element) {
  if (!element?.closest) return false;
  if (element.closest(MOBILE_QUICK_ADD_SELECTOR)) return true;
  const dialog = element.closest('#quick-add-dialog');
  return Boolean(dialog?.querySelector?.('wc-quick-add-media, [data-wc-quick-add-mobile]'));
}

/**
 * @param {ParentNode | null | undefined} root
 * @param {string} [message]
 */
export function setQuickAddStatus(root, message = '') {
  if (!root?.querySelector) return;
  const status = root.querySelector('[data-wc-quick-add-status]');
  const retry = root.querySelector('[data-wc-quick-add-retry]');
  const hasMessage = Boolean(message);
  if (status) {
    status.hidden = !hasMessage;
    status.textContent = message;
  }
  if (retry) retry.hidden = !hasMessage;
}

const PACK_UNIT_PATTERN = /\b(12|6|3|1)\s*-?\s*pack\b/gi;

/**
 * @param {unknown} value
 * @returns {number[]}
 */
export function packUnitsFromLabel(value) {
  const units = [];
  const text = String(value || '');
  for (const match of text.matchAll(PACK_UNIT_PATTERN)) {
    const unit = Number(match[1]);
    if (!units.includes(unit)) units.push(unit);
  }
  return units;
}

/**
 * @param {unknown} alt
 * @returns {boolean}
 */
export function isExcludedAlt(alt) {
  const text = String(alt || '').toLowerCase();
  return text.includes('on model') || text.includes('packaging reference');
}

/**
 * @param {string | number} productId
 * @param {string} color
 * @param {number} packUnits
 * @returns {string}
 */
export function mediaIdentity(productId, color, packUnits) {
  return `${productId}|${color || ''}|${packUnits || 0}`;
}

/**
 * @param {Record<string, {product?: string}> | null | undefined} pairs
 * @param {string} color
 * @param {number} packUnits
 * @param {boolean} packIndependent
 * @returns {string}
 */
export function mappedProductId(pairs, color, packUnits, packIndependent) {
  if (!pairs) return '';
  if (packUnits) {
    const packId = pairs[`${packUnits}-pack`]?.product;
    if (packId) return String(packId);
    return '';
  }
  if (packIndependent && color && pairs[color]?.product) return String(pairs[color].product);
  return String(pairs[color]?.product || pairs.default?.product || '');
}

/**
 * @param {{packChoiceCount?: number, packUnits?: number}} input
 * @returns {boolean}
 */
export function isPackIndependent(input) {
  return !input.packChoiceCount || input.packChoiceCount <= 1 || !input.packUnits;
}

/**
 * @param {object[]} images
 * @param {string} color
 * @param {number} packUnits
 * @returns {object | 'ambiguous' | null}
 */
export function matchMetadata(images, color, packUnits) {
  const candidates = images.filter((image) => matchesPackImage(image, color, packUnits));
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) return 'ambiguous';
  return null;
}

/**
 * @param {object} image
 * @param {string} color
 * @param {number} packUnits
 * @returns {boolean}
 */
export function matchesPackImage(image, color, packUnits) {
  if (image?.excluded) return false;
  if (color && image.color && image.color !== color) return false;
  const units = Array.isArray(image.packUnits) ? image.packUnits.map(Number) : [];
  if (packUnits > 0 && units.length === 0) return false;
  if (packUnits > 0 && (units.length !== 1 || units[0] !== packUnits)) return false;
  return true;
}

/**
 * @typedef {object} PackMediaInput
 * @property {string | number} productId
 * @property {string} [color]
 * @property {number} [packUnits]
 * @property {number} [packChoiceCount]
 * @property {Record<string, {product?: string}> | null} [pairs]
 * @property {object[]} [images]
 */

/**
 * @param {PackMediaInput} input
 * @returns {{mediaId: string, reason: string}}
 */
export function resolvePackMedia(input) {
  const images = Array.isArray(input.images) ? input.images : [];
  const byId = new Map(images.map((image) => [String(image.id), image]));
  const color = input.color || '';
  const packUnits = Number(input.packUnits) || 0;
  const packIndependent = isPackIndependent(input);
  if (packUnits) return resolveSelectedPack(input.pairs, byId, images, color, packUnits);
  const mappedId = mappedProductId(input.pairs, color, packUnits, packIndependent);
  return resolveFromMap(mappedId, byId, images, color, packUnits, packIndependent);
}

/**
 * @param {Record<string, {product?: string}> | null | undefined} pairs
 * @param {Map<string, object>} byId
 * @param {object[]} images
 * @param {string} color
 * @param {number} packUnits
 * @returns {{mediaId: string, reason: string}}
 */
function resolveSelectedPack(pairs, byId, images, color, packUnits) {
  const packId = pairs?.[`${packUnits}-pack`]?.product;
  if (packId) {
    const mappedId = String(packId);
    if (byId.has(mappedId) && !byId.get(mappedId)?.excluded) return { mediaId: mappedId, reason: 'map' };
    if (!byId.has(mappedId)) return { mediaId: '', reason: 'mapped-missing' };
  }
  const matched = matchMetadata(images, color, packUnits);
  if (matched === 'ambiguous') return { mediaId: '', reason: 'ambiguous' };
  if (matched?.id) return { mediaId: String(matched.id), reason: 'metadata' };
  return { mediaId: '', reason: 'missing' };
}

/**
 * @param {string} mappedId
 * @param {Map<string, object>} byId
 * @param {object[]} images
 * @param {string} color
 * @param {number} packUnits
 * @param {boolean} packIndependent
 * @returns {{mediaId: string, reason: string}}
 */
function resolveFromMap(mappedId, byId, images, color, packUnits, packIndependent) {
  if (mappedId && byId.has(mappedId) && !byId.get(mappedId)?.excluded) {
    return { mediaId: mappedId, reason: 'map' };
  }
  if (mappedId && !byId.has(mappedId)) return { mediaId: '', reason: 'mapped-missing' };
  const matched = matchMetadata(images, color, packUnits);
  if (matched === 'ambiguous') return { mediaId: '', reason: 'ambiguous' };
  if (matched?.id) return { mediaId: String(matched.id), reason: 'metadata' };
  return resolveIndependent(byId, packIndependent, mappedId);
}

/**
 * @param {Map<string, object>} byId
 * @param {boolean} packIndependent
 * @param {string} mappedId
 * @returns {{mediaId: string, reason: string}}
 */
function resolveIndependent(byId, packIndependent, mappedId) {
  if (!packIndependent || !mappedId) return { mediaId: '', reason: 'missing' };
  const image = byId.get(mappedId);
  if (image && !image.excluded) return { mediaId: mappedId, reason: 'pack-independent' };
  return { mediaId: '', reason: 'missing' };
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {{color: string, packUnits: number}}
 */
export function selectionFromPicker(root) {
  if (!root?.querySelectorAll) return { color: '', packUnits: 0 };
  let color = '';
  let packUnits = 0;
  for (const fieldset of root.querySelectorAll('fieldset.variant-option')) {
    if (fieldset.hasAttribute('data-option-pack')) packUnits = packUnitsFromFieldset(fieldset);
    else if (isColorFieldset(fieldset)) color = handleize(selectedFieldValue(fieldset));
  }
  return { color, packUnits };
}

/**
 * @param {Element} fieldset
 * @returns {number}
 */
function packUnitsFromFieldset(fieldset) {
  const packed = fieldset.querySelector('input:checked[data-pack-units], option[selected][data-pack-units]');
  const fromAttr = Number(packed?.getAttribute?.('data-pack-units') ?? packed?.dataset?.packUnits);
  if (fromAttr > 0) return fromAttr;
  const selected = fieldset.querySelector('input:checked, option[selected]');
  const fromValue = packUnitsFromLabel(selected?.value)[0];
  if (fromValue) return fromValue;
  const label = selected?.closest?.('label') || fieldset;
  return packUnitsFromLabel(label?.textContent || selected?.getAttribute?.('aria-label'))[0] || 0;
}

/**
 * @param {Element} fieldset
 * @returns {string}
 */
function selectedFieldValue(fieldset) {
  return fieldset.querySelector('input:checked, option[selected]')?.value || '';
}

/**
 * @param {Element} fieldset
 * @returns {boolean}
 */
function isColorFieldset(fieldset) {
  const name = String(fieldset.getAttribute('data-option-name') || '').toLowerCase();
  return fieldset.classList.contains('variant-option--swatches') || name === 'color' || name === 'colour';
}

/**
 * @param {string} currentId
 * @param {string} nextId
 * @returns {boolean}
 */
export function shouldReuseMedia(currentId, nextId) {
  return Boolean(currentId) && currentId === nextId;
}

/**
 * @param {HTMLElement} host
 * @param {{mediaId: string, image?: object, unavailableText?: string}} resolved
 * @param {{activeId: string, revision: number, getRevision: () => number}} session
 * @returns {{mutated: boolean, activeId: string}}
 */
export function applyPackMedia(host, resolved, session) {
  const nextId = resolved.mediaId || '';
  if (shouldReuseMedia(session.activeId, nextId)) return { mutated: false, activeId: session.activeId };
  const frame = host.querySelector('[data-wc-quick-add-frame]');
  const status = host.querySelector('[data-wc-quick-add-media-status]');
  if (!frame) return { mutated: false, activeId: session.activeId };
  if (!nextId) return keepCurrentImage(frame, status, resolved.unavailableText, session);
  return showImage(frame, status, resolved, session);
}

/**
 * @param {Element} frame
 * @param {Element | null} status
 * @param {string | undefined} text
 * @param {{activeId: string, revision: number}} session
 * @returns {{mutated: boolean, activeId: string}}
 */
function keepCurrentImage(frame, status, text, session) {
  frame.removeAttribute('data-loading');
  if (status && text) {
    status.hidden = false;
    status.textContent = text;
  }
  return { mutated: Boolean(text), activeId: session.activeId || currentMediaId(frame) };
}

/**
 * @param {Element} frame
 * @returns {string}
 */
function currentMediaId(frame) {
  for (const image of frame.querySelectorAll('img')) {
    if (!image.hidden && image.dataset?.mediaId) return image.dataset.mediaId;
  }
  return frame.querySelector('img')?.dataset?.mediaId || '';
}

/**
 * @param {Element} frame
 * @param {Element | null} status
 * @param {{mediaId: string, image?: object}} resolved
 * @param {{revision: number, getRevision: () => number}} session
 * @returns {{mutated: boolean, activeId: string}}
 */
function showImage(frame, status, resolved, session) {
  if (status) {
    status.hidden = true;
    status.textContent = '';
  }
  const image = ensureImage(frame, resolved);
  revealWhenReady(image, frame, session);
  return { mutated: true, activeId: resolved.mediaId };
}

/**
 * @param {Element} frame
 * @param {object[] | undefined} images
 */
export function preloadPackImages(frame, images) {
  if (!frame) return;
  for (const image of images || []) {
    if (!image?.id || image.excluded) continue;
    ensureImage(frame, { mediaId: String(image.id), image });
  }
}

/**
 * @param {Element} frame
 * @param {{mediaId: string, image?: object}} resolved
 * @returns {HTMLImageElement}
 */
function ensureImage(frame, resolved) {
  const existing = frame.querySelector(`img[data-media-id="${resolved.mediaId}"]`);
  if (existing instanceof HTMLImageElement) return existing;
  const image = document.createElement('img');
  image.dataset.mediaId = resolved.mediaId;
  image.alt = resolved.image?.alt || '';
  image.width = resolved.image?.width || 800;
  image.height = resolved.image?.height || 1000;
  image.decoding = 'sync';
  image.src = resolved.image?.src || '';
  if (resolved.image?.srcset) image.srcset = resolved.image.srcset;
  image.hidden = true;
  frame.append(image);
  return image;
}

/**
 * @param {HTMLImageElement} image
 * @param {Element} frame
 */
function showOnly(image, frame) {
  for (const img of frame.querySelectorAll('img')) img.hidden = img !== image;
  frame.removeAttribute('data-loading');
}

/**
 * @param {Element} frame
 * @param {string} previousId
 */
function restorePreviousImage(frame, previousId) {
  if (!previousId) return;
  const previous = frame.querySelector(`img[data-media-id="${previousId}"]`);
  if (previous) previous.hidden = false;
}

/**
 * @param {HTMLImageElement} image
 * @param {Element} frame
 * @param {{activeId: string, revision: number, getRevision: () => number}} session
 */
function revealWhenReady(image, frame, session) {
  if (session.getRevision() !== session.revision) return;
  showOnly(image, frame);
  if (image.complete && image.naturalWidth > 0) return;
  image.addEventListener('error', () => {
    if (session.getRevision() !== session.revision) return;
    image.hidden = true;
    restorePreviousImage(frame, session.activeId);
    frame.removeAttribute('data-loading');
  }, { once: true });
}

/**
 * @param {string | undefined} raw
 * @returns {PackMediaInput | null}
 */
export function parseMediaManifest(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

class WCQuickAddMedia extends HTMLElement {
  #revision = 0;
  #activeId = '';
  /** @type {PackMediaInput | null} */
  #manifest = null;
  /** @type {AbortController | null} */
  #listener = null;

  connectedCallback() {
    this.#listener = new AbortController();
    const root = this.closest('#quick-add-dialog, [data-wc-quick-add-mobile]') || this;
    root.addEventListener('change', this.#onPickerChange, { signal: this.#listener.signal });
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal: this.#listener.signal });
    this.#syncFromDom();
    requestAnimationFrame(() => this.#syncFromDom());
  }

  disconnectedCallback() {
    this.#revision += 1;
    this.#listener?.abort();
  }

  #onPickerChange = () => {
    this.#applyFromPicker();
  };

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!this.#isEventForThis(event)) return;
    this.#adoptManifest(event.detail?.data);
    this.#preload();
    this.#applyFromPicker();
    requestAnimationFrame(() => this.#syncFromDom());
  };

  /**
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #isEventForThis(event) {
    const root = this.closest('#quick-add-dialog, dialog, [data-wc-quick-add-mobile]') || this;
    const target = event.target;
    if (target instanceof Node && (root === target || root.contains(target))) return true;
    const eventProductId = event.detail?.data?.productId;
    return Boolean(eventProductId) && String(eventProductId) === String(this.dataset.productId);
  }

  /**
   * @param {{html?: ParentNode, newProduct?: {id: string}} | undefined} data
   */
  #adoptManifest(data) {
    if (!data?.html?.querySelector) return;
    const next = parseMediaManifest(data.html.querySelector('[data-wc-quick-add-media]')?.textContent);
    if (!next) return;
    this.#manifest = next;
    if (data.newProduct?.id) this.dataset.productId = String(data.newProduct.id);
  }

  #syncFromDom() {
    const parsed = parseMediaManifest(this.querySelector('[data-wc-quick-add-media]')?.textContent);
    if (parsed) this.#manifest = parsed;
    const existing = this.querySelector('[data-wc-quick-add-frame] img[data-media-id]');
    if (!this.#activeId && existing instanceof HTMLImageElement) {
      this.#activeId = existing.dataset.mediaId || '';
    }
    this.#preload();
    this.#applyFromPicker();
  }

  #preload() {
    preloadPackImages(this.querySelector('[data-wc-quick-add-frame]'), this.#manifest?.images);
  }

  #applyFromPicker() {
    const pickerRoot =
      this.closest(MOBILE_QUICK_ADD_SELECTOR) || this.closest('#quick-add-dialog, dialog') || this;
    const selection = selectionFromPicker(pickerRoot);
    const input = { ...this.#manifest, ...selection };
    if (!selection.packUnits && this.#manifest?.packUnits) input.packUnits = this.#manifest.packUnits;
    if (!selection.color && this.#manifest?.color) input.color = this.#manifest.color;
    this.#apply(input);
  }

  /**
   * @param {PackMediaInput | null} input
   */
  #apply(input) {
    if (!input) return;
    const resolved = resolvePackMedia(input);
    if (shouldReuseMedia(this.#activeId, resolved.mediaId)) return;
    const image = (input.images || []).find((item) => String(item.id) === resolved.mediaId);
    const revision = ++this.#revision;
    const result = applyPackMedia(this, { ...resolved, image, unavailableText: input.unavailableText }, {
      activeId: this.#activeId,
      revision,
      getRevision: () => this.#revision,
    });
    this.#activeId = result.activeId;
  }
}

if (!customElements.get('wc-quick-add-media')) {
  customElements.define('wc-quick-add-media', WCQuickAddMedia);
}
